import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import {
  cancelTwoFactorSetup,
  confirmTwoFactorSetup,
  initTwoFactorSetup,
} from '../../store/general/general-actions';
import { commitTwoFactorLogin } from '../../store/general/general-slice';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import CustomLoader from '@common/components/Feedback/CustomLoader';
import HttpError, { isHttpError } from '@common/interfaces/api/http-error';
import ValidationResult from '@common/interfaces/api/validation-result';
import CurrentUser from '../../models/general/current-user';
import SwitchInput from 'components/forms/SwitchInput';
import IconButton from '@mui/material/IconButton';
import { CopyIcon, HideInputIcon, ShowInputIcon } from '@common/utils/icons/icons';

interface TwoFactorSetupProps {
  pendingToken?: string;
  variant: 'dialog' | 'page';
  onComplete: () => void;
  onCancel?: () => void;
}

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
  pendingToken,
  variant,
  onComplete,
  onCancel,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [otpAuthUri, setOtpAuthUri] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [pendingLoginCommit, setPendingLoginCommit] = useState<{
    token: string;
    user: CurrentUser;
  } | null>(null);
  const [isSecretVisible, setIsSecretVisible] = useState(false);

  const [initStatus, setInitStatus] = useState<HttpFetchStatus>(HttpFetchStatus.Idle);
  const [initError, setInitError] = useState<HttpError | null>(null);

  const [confirmStatus, setConfirmStatus] = useState<HttpFetchStatus>(HttpFetchStatus.Idle);
  const [confirmError, setConfirmError] = useState<HttpError | null>(null);

  useEffect(() => {
    let isMounted = true;
    setInitStatus(HttpFetchStatus.Loading);
    setInitError(null);

    dispatch(initTwoFactorSetup(pendingToken)).then((result) => {
      if (!isMounted) {
        return;
      }

      if (initTwoFactorSetup.fulfilled.match(result)) {
        // Using inline state rather than backend state to avoid saving secrets in state
        setOtpAuthUri(result.payload.otpAuthUri);
        setSecret(result.payload.secret);
        setInitStatus(HttpFetchStatus.Success);
        return;
      }

      setInitStatus(HttpFetchStatus.Failed);
      const payload = result.payload as HttpError | ValidationResult[] | undefined;
      if (payload != null && isHttpError(payload)) {
        setInitError(payload);
      }
    });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmHandler = async () => {
    setConfirmStatus(HttpFetchStatus.Loading);
    setConfirmError(null);

    const result = await dispatch(confirmTwoFactorSetup({ otp: code, pendingToken }));

    if (confirmTwoFactorSetup.fulfilled.match(result)) {
      setConfirmStatus(HttpFetchStatus.Success);
      setBackupCodes(result.payload.backupCodes);
      if (result.payload.token != null && result.payload.user != null) {
        setPendingLoginCommit({
          token: result.payload.token,
          user: result.payload.user as CurrentUser,
        });
      }
      return;
    }

    setConfirmStatus(HttpFetchStatus.Failed);
    const payload = result.payload as HttpError | ValidationResult[] | undefined;
    if (payload != null && isHttpError(payload)) {
      setConfirmError(payload);
    }
  };

  const cancelHandler = () => {
    dispatch(cancelTwoFactorSetup());
    onCancel?.();
  };

  const continueHandler = () => {
    if (pendingLoginCommit != null) {
      dispatch(commitTwoFactorLogin(pendingLoginCommit));
    }
    onComplete();
  };

  const toggleSecretVisibilityHandler = () => {
    setIsSecretVisible((prevState) => !prevState);
  };

  const copySecretClickHandler = () => {
    if (secret != null) navigator.clipboard.writeText(secret);
  };

  const secretInputProps = () => (
    <Stack direction="row" spacing={1}>
      <IconButton onClick={copySecretClickHandler}>
        <CopyIcon />
      </IconButton>
      <IconButton onClick={toggleSecretVisibilityHandler}>
        {isSecretVisible ? <HideInputIcon /> : <ShowInputIcon />}
      </IconButton>
    </Stack>
  );

  const content = (
    <Stack spacing={2} width="100%">
      {backupCodes == null ? (
        <React.Fragment>
          <Typography variant="h6">{t('title-two-factor-setup-scan')}</Typography>

          {initStatus === HttpFetchStatus.Loading ? (
            <CustomLoader loadingMessage={t('loading-two-factor-setup')} />
          ) : (
            <React.Fragment>
              {otpAuthUri != null && (
                <Stack
                  alignItems="center"
                  role="img"
                  aria-label={t('alt-two-factor-qr')}
                  spacing={2}
                >
                  <CustomAlert severity="info" header={t('info-setup-two-factor-auth')} />
                  <QRCodeSVG value={otpAuthUri} size={200} />
                </Stack>
              )}
              {secret != null && (
                <TextField
                  label={t('label-two-factor-manual-entry')}
                  value={secret}
                  disabled
                  type={isSecretVisible ? 'text' : 'password'}
                  InputProps={{ endAdornment: secretInputProps() }}
                />
              )}
              {initError != null && (
                <CustomAlert
                  header={t('error-two-factor-setup-init-failed')}
                  textMessages={[t(initError.message)]}
                  severity="error"
                />
              )}
              <TextField
                label={t('input-label-authenticator-code')}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                fullWidth
              />
              {confirmError != null && (
                <CustomAlert
                  header={t('error-two-factor-invalid-code')}
                  textMessages={[t(confirmError.message)]}
                  severity="error"
                />
              )}
              <Stack direction="row" spacing={2} width="100%" justifyContent="flex-end">
                {variant === 'dialog' && (
                  <Button onClick={cancelHandler} variant="text" color="inherit">
                    {t('button-cancel')}
                  </Button>
                )}
                <Button
                  onClick={confirmHandler}
                  variant="contained"
                  disabled={code.length === 0 || confirmStatus === HttpFetchStatus.Loading}
                >
                  {t('button-continue')}
                </Button>
              </Stack>
            </React.Fragment>
          )}
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Typography variant="h6">{t('title-two-factor-backup-codes')}</Typography>
          <Typography variant="body2">{t('info-two-factor-backup-codes')}</Typography>
          <Stack spacing={0.5}>
            {backupCodes.map((backupCode) => (
              <Typography key={backupCode} fontFamily="monospace">
                {backupCode}
              </Typography>
            ))}
          </Stack>
          <SwitchInput
            label={t('input-label-saved-backup-codes')}
            isChecked={acknowledged}
            onChange={() => setAcknowledged((prevState) => !prevState)}
          />
          <Button onClick={continueHandler} variant="contained" disabled={!acknowledged}>
            {t('button-continue')}
          </Button>
        </React.Fragment>
      )}
    </Stack>
  );

  if (variant === 'dialog') {
    return (
      <Dialog open maxWidth="sm" disableEscapeKeyDown>
        <DialogContent>{content}</DialogContent>
      </Dialog>
    );
  }

  return content;
};

export default TwoFactorSetup;
